import type { Match } from "navigo";
import type { ChildDom } from "vanjs-core";
import { reactive } from "vanjs-ext";
import { relativeTimeString } from "../locale";
import type { Route, RouteContext } from "../router";
import { centeredBox } from "../van/centered-box";
import { button, div, h1, label, p } from "../van/van-tags";
import { sp2Placeholder } from "./sp2";

export class Sp2CmdsPage implements Route {
	async #getSp2(imei: string): Promise<void> {
		this.#ctx.error.clear();

		const req = this.#ctx.api.get(`/smartplug2s/${imei}`);
		req.setSignedInUserAuth();

		const res = await this.#ctx.loadingScreen.waitFor(req.do());
		if (res.ok) {
			this.#ctx.error.clear();
			this.#reactive.sp2 = await res.json();
		} else {
			this.#ctx.error.setText(await res.text());
		}
	}

	#onClickRefresh(): void {
		this.#getSp2(this.#reactive.sp2.Imei);
	}

	async refresh(match: Match): Promise<void> {
		if (!match?.data?.imei) throw "missing path param: imei";
		const imei = match.data.imei;
		this.#getSp2(imei);
	}

	#ctx: RouteContext;
	#reactive = reactive({
		sp2: sp2Placeholder,
	});

	ChildDom: ChildDom;

	constructor(ctx: RouteContext) {
		this.#ctx = ctx;
		this.ChildDom = centeredBox(
			{ size: "narrow" },
			h1({ class: "title is-4" }, "SmartPlug2 Commands"),
			p(
				{ class: "subtitle is-6" },
				() => `IMEI ${this.#reactive.sp2.Imei}`,
			),
			div(
				{ class: "field" },
				label(
					{
						class: "label",
					},
					() =>
						`Last activity: ${relativeTimeString(this.#reactive.sp2.LastUploadAt)}`,
				),
			),
			div(
				{ class: "field" },
				label(
					{
						class: "label",
					},
					() => `Load: ${this.#reactive.sp2.Load} W`,
				),
			),
			div(
				{ class: "field" },
				label(
					{
						class: "label",
					},
					() =>
						`Signal strength: ${this.#reactive.sp2.SignalStrength}%`,
				),
			),
			div(
				{ class: "buttons" },
				button(
					{
						class: "button is-primary is-fullwidth",
						onclick: () =>
							this.#ctx.router.down("/set-output-power"),
					},
					"Set Output Power (A1)",
				),
				button(
					{
						class: "button is-primary is-fullwidth",
						onclick: () => this.#ctx.router.down("/get-status"),
					},
					"Get Status (A2)",
				),
				button(
					{
						class: "button is-primary is-fullwidth",
						onclick: () => this.#ctx.router.down("/set-beep"),
					},
					"Set Beep (A3)",
				),
				button(
					{
						class: "button is-primary is-fullwidth",
						onclick: () => this.#ctx.router.down("/set-load-alarm"),
					},
					"Set Load Alarm (A6)",
				),
				button(
					{
						class: "button is-primary is-fullwidth",
						onclick: () => this.#ctx.router.down("/get-imsi"),
					},
					"Get IMSI (A9)",
				),
				button(
					{
						class: "button is-primary is-fullwidth",
						onclick: () => this.#ctx.router.down("/get-fw"),
					},
					"Get Firmware (AA)",
				),
				button(
					{
						class: "button is-primary is-fullwidth",
						onclick: () => this.#ctx.router.down("/set-fw"),
					},
					"Set Firmware (AC)",
				),
				button(
					{
						class: "button is-primary",
						onclick: () => this.#onClickRefresh(),
					},
					"Refresh",
				),
				button(
					{
						class: "button is-danger",
						onclick: () => this.#ctx.router.up(),
					},
					"Back",
				),
			),
		);
	}
}
